import { useState, useEffect, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '../../components/Icons';
import { storage, db } from '../../firebase';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import {
  collection, addDoc, deleteDoc, doc, serverTimestamp, query, orderBy,
  limit, getDocs, startAfter, getCountFromServer, updateDoc, where, writeBatch
} from "firebase/firestore";
import { useAuth } from '../../contexts/AuthContext';

const ImageManagementPage = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageTitle, setImageTitle] = useState('');
  const [imageCategory, setImageCategory] = useState('지붕공사');
  const [imageDescription, setImageDescription] = useState('');
  const [editingImage, setEditingImage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { currentUser } = useAuth();

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [totalImages, setTotalImages] = useState(0);
  const [pageDirection, setPageDirection] = useState('initial');
  // 각 페이지의 마지막 문서를 저장하는 객체 (페이지 번호: 문서)
  const [pageLastDocs, setPageLastDocs] = useState({}); 

  useEffect(() => {
    const getTotalImages = async () => {
      try {
        const coll = collection(db, "constructionImages");
        const snapshot = await getCountFromServer(coll);
        setTotalImages(snapshot.data().count);
      } catch (error) {
        console.error("Error fetching total image count: ", error);
      }
    };
    getTotalImages();
  }, []);

  // 최초 로딩 및 페이지 변경 시 데이터 가져오기
  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      try {
        const imagesCollectionRef = collection(db, "constructionImages");
        
        // 쿼리 생성
        let q;
        const lastVisible = pageLastDocs[currentPage - 1]; // 이전 페이지의 마지막 문서 (커서)
        if (currentPage === 1 || !lastVisible) {
          // 첫 페이지
          q = query(imagesCollectionRef, orderBy("uploadedAt", "desc"), limit(itemsPerPage));
        } else {
          // 다음 페이지
          q = query(imagesCollectionRef, orderBy("uploadedAt", "desc"), startAfter(lastVisible), limit(itemsPerPage));
        }

        const documentSnapshots = await getDocs(q);
        const newImages = documentSnapshots.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
        setImages(newImages);

        // 현재 페이지의 마지막 문서를 다음 페이지를 위한 커서로 저장
        if (documentSnapshots.docs.length > 0) {
          const lastDoc = documentSnapshots.docs[documentSnapshots.docs.length - 1];
          setPageLastDocs(prev => ({ ...prev, [currentPage]: lastDoc }));
        }

      } catch (error) {
        console.error("Error fetching images: ", error);
        alert("이미지 목록을 불러오는 데 실패했습니다.");
      }
      setLoading(false);
    };

    // 총 이미지 수가 설정된 후에 데이터를 가져오도록 함
    if (totalImages > 0) {
        fetchImages();
    } else {
        setImages([]); // 총 이미지가 0이면 목록을 비움
        setLoading(false);
    }
  }, [currentPage, totalImages]); // currentPage 또는 totalImages가 변경될 때마다 실행

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const setAsHeroImage = async (newHeroImage) => {
    if (!currentUser) { alert('로그인이 필요합니다.'); return; }
    if (newHeroImage.isHeroImage) { // 이미 배경이미지인 경우
        alert('이 이미지는 이미 배경 이미지로 설정되어 있습니다.');
        return;
    }
    if (!window.confirm(`'${newHeroImage.title}' 이미지를 홈페이지 배경으로 설정하시겠습니까?`)) {
        return;
    }

    const imagesRef = collection(db, "constructionImages");
    const batch = writeBatch(db);

    try {
        // 1. 현재 설정된 배경 이미지(hero)를 찾는다.
        const q = query(imagesRef, where("isHeroImage", "==", true), limit(1));
        const currentHeroSnapshot = await getDocs(q);

        // 2. 현재 배경 이미지가 있다면, isHeroImage를 false로 업데이트한다.
        if (!currentHeroSnapshot.empty) {
            const currentHeroDoc = currentHeroSnapshot.docs[0];
            const currentHeroRef = doc(db, "constructionImages", currentHeroDoc.id);
            batch.update(currentHeroRef, { isHeroImage: false });
        }

        // 3. 새로 선택된 이미지를 isHeroImage: true로 업데이트한다.
        const newHeroRef = doc(db, "constructionImages", newHeroImage.id);
        batch.update(newHeroRef, { isHeroImage: true });

        // 4. 모든 업데이트를 한번에 실행한다.
        await batch.commit();

        // 5. 로컬 상태를 업데이트하여 UI에 즉시 반영
        setImages(prevImages => prevImages.map(img => {
            if (img.id === newHeroImage.id) return { ...img, isHeroImage: true };
            if (img.isHeroImage) return { ...img, isHeroImage: false };
            return img;
        }));

        alert("홈페이지 배경 이미지가 변경되었습니다.");

    } catch (error) {
        console.error("Error setting hero image: ", error);
        alert("배경 이미지 설정 중 오류가 발생했습니다.");
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) { alert('이미지를 선택해주세요.'); return; }
    if (!currentUser) { alert('로그인이 필요합니다.'); return; }
    setIsUploading(true); setUploadProgress(0);
    const uniqueFileName = `${Date.now()}-${selectedFile.name}`;
    const imageStorageRef = storageRef(storage, `constructionImages/${uniqueFileName}`);
    const uploadTask = uploadBytesResumable(imageStorageRef, selectedFile);

    uploadTask.on('state_changed',
      (snapshot) => setUploadProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
      (error) => {
        console.error("Upload failed:", error);
        alert(`업로드 실패: ${error.message}`);
        setIsUploading(false);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const newImageDocData = {
            title: imageTitle,
            category: imageCategory,
            description: imageDescription || "",
            imageUrl: downloadURL,
            fileName: uniqueFileName,
            uploadedAt: serverTimestamp(),
            uploaderUid: currentUser.uid,
            uploaderEmail: currentUser.email,
            isFeatured: false
          };
          const docRef = await addDoc(collection(db, "constructionImages"), newImageDocData);

          try {
            const expireDate = new Date();
            expireDate.setDate(expireDate.getDate() + 90); // 90일 후 만료

            await addDoc(collection(db, "activities"), {
              type: "image_upload",
              text: `이미지 업로드: "${newImageDocData.title}" (카테고리: ${newImageDocData.category})`,
              timestamp: serverTimestamp(),
              expireAt: expireDate, // TTL 필드 추가
              relatedId: docRef.id,
              userId: currentUser.uid,
              details: { imageTitle: newImageDocData.title, imageCategory: newImageDocData.category, fileName: newImageDocData.fileName }
            });
          } catch (activityError) {
            console.error("Error logging image upload activity: ", activityError);
          }

          alert(`'${selectedFile.name}' 업로드 성공!`);
          setTotalImages(prev => prev + 1);
          if (currentPage !== 1) {
            setCurrentPage(1);
            setPageDirection('initial');
          } else {
            setPageDirection('initial');
            if (totalImages === 0) setTotalImages(1);
          }

        } catch (error) {
          console.error("Error saving metadata: ", error);
          alert(`메타데이터 저장 실패: ${error.message}`);
        } finally {
          setSelectedFile(null);
          setImageTitle('');
          setImageCategory('지붕공사');
          setImageDescription('');
          if (fileInputRef.current) fileInputRef.current.value = "";
          setShowUploadModal(false);
          setIsUploading(false);
          setUploadProgress(0);
        }
      }
    );
  };

  const handleDelete = async (imageId, fileName) => { 
    if (!currentUser) { alert('로그인이 필요합니다.'); return; }
    if (window.confirm("정말로 이 이미지를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.")) { 
      try {
        const deletedImage = images.find(img => img.id === imageId); 
        await deleteDoc(doc(db, "constructionImages", imageId));
        if (fileName) {
          const imageFileRef = storageRef(storage, `constructionImages/${fileName}`);
          await deleteObject(imageFileRef);
        }
        
        try {
            const expireDate = new Date();
            expireDate.setDate(expireDate.getDate() + 90);
            await addDoc(collection(db, "activities"), {
              type: "image_delete",
              text: `이미지 삭제: "${deletedImage?.title || fileName}"`,
              timestamp: serverTimestamp(),
              expireAt: expireDate,
              relatedId: imageId,
              userId: currentUser.uid,
              details: { fileName: fileName }
            });
        } catch (activityError) {
            console.error("Error logging image delete activity: ", activityError);
        }

        alert(`이미지 ID ${imageId} 삭제 완료.`);
        // 총 이미지 개수를 업데이트하면 useEffect가 자동으로 현재 페이지를 다시 불러옴
        setTotalImages(prev => prev - 1); 

        // 만약 현재 페이지의 마지막 아이템을 삭제해서 페이지가 비게 되면 이전 페이지로 이동
        if (images.length === 1 && currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
      } catch (error) { 
        console.error("Error deleting image: ", error); 
        alert(`이미지 삭제 실패: ${error.message}`); 
      }
    }
  };

  const toggleFeaturedImage = async (imageId, currentIsFeatured) => {
    if (!currentUser) { alert('로그인이 필요합니다.'); return; }
    const imageDocRef = doc(db, "constructionImages", imageId);
    try {
      await updateDoc(imageDocRef, { isFeatured: !currentIsFeatured });
      setImages(prevImages => prevImages.map(img =>
        img.id === imageId ? { ...img, isFeatured: !currentIsFeatured } : img
      ));
      alert(`이미지 ID ${imageId}의 대표 프로젝트 설정이 변경되었습니다.`);
      try {
        const expireDate = new Date();
        expireDate.setDate(expireDate.getDate() + 90);
        const image = images.find(img => img.id === imageId);

        await addDoc(collection(db, "activities"), {
          type: "image_feature_toggle",
          text: `대표 프로젝트 ${!currentIsFeatured ? '지정' : '해제'}: "${image?.title || imageId}"`,
          timestamp: serverTimestamp(),
          expireAt: expireDate,
          relatedId: imageId,
          userId: currentUser.uid,
          details: { imageId: imageId, isFeatured: !currentIsFeatured }
        });
      } catch (activityError) {
        console.error("Error logging feature toggle activity: ", activityError);
      }
    } catch (error) {
      console.error("Error updating featured status: ", error);
      alert("대표 프로젝트 설정 변경에 실패했습니다.");
    }
  };

  const openEditModal = (image) => {
    setEditingImage(image);
    setImageTitle(image.title || '');
    setImageCategory(image.category || '지붕공사');
    setImageDescription(image.description || '');
    setShowEditModal(true);
  };

  const handleUpdateImage = async (e) => {
    e.preventDefault();
    if (!editingImage) return;

    const imageDocRef = doc(db, "constructionImages", editingImage.id);
    const updatedData = {
      title: imageTitle,
      category: imageCategory,
      description: imageDescription
    };

    try {
      await updateDoc(imageDocRef, updatedData);
      alert('이미지 정보가 성공적으로 수정되었습니다.');
      setShowEditModal(false);
      setEditingImage(null);
      setImages(prev => prev.map(img => img.id === editingImage.id ? { ...img, ...updatedData } : img));

      const expireDate = new Date();
      expireDate.setDate(expireDate.getDate() + 90);

      await addDoc(collection(db, 'activities'), {
        type: 'image_update',
        text: `이미지 정보 수정: "${updatedData.title}"`,
        timestamp: serverTimestamp(),
        expireAt: expireDate,
        relatedId: editingImage.id,
        userId: currentUser?.uid,
        details: { imageId: editingImage.id, updatedTitle: updatedData.title }
      });

    } catch (error) {
      console.error("Error updating image document: ", error);
      alert("이미지 정보 수정에 실패했습니다.");
    }
  };

  const totalPages = Math.ceil(totalImages / itemsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages && !loading) {
        setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1 && !loading) {
      setCurrentPage(prev => prev - 1);
    }
  };

  return (
    <>
      <title>이미지 관리 - 관리자</title>
      <div>
        <div className="flex justify-between items-center mb-6"><h1 className="text-3xl font-bold text-gray-800 dark:text-white">이미지 관리</h1><button onClick={() => setShowUploadModal(true)} className="bg-sky-600 hover:bg-sky-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition-colors" disabled={isUploading}>새 이미지 업로드</button></div>
        {showUploadModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-white">새 이미지 업로드</h2>
              <form onSubmit={handleUpload} className="space-y-4">
                <div>
                  <label htmlFor="imageFile" className="block text-sm font-medium text-gray-700 dark:text-gray-300">이미지 파일</label>
                  <input type="file" id="imageFile" ref={fileInputRef} onChange={handleFileChange} accept="image/*" required className="mt-1 block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-sky-50 dark:file:bg-sky-700 file:text-sky-700 dark:file:text-sky-300 hover:file:bg-sky-100 dark:hover:file:bg-sky-600" disabled={isUploading} />
                  {selectedFile && !isUploading && <img src={URL.createObjectURL(selectedFile)} alt="미리보기" className="mt-2 max-h-40 rounded-md" />}
                  {isUploading && (<div className="mt-2"><div className="w-full bg-gray-200 dark:bg-slate-600 rounded-full h-2.5"><div className="bg-sky-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div></div><p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">{Math.round(uploadProgress)}%</p></div>)}
                </div>
                <div><label htmlFor="imageTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300">제목 (필수)</label><input type="text" id="imageTitle" value={imageTitle} onChange={(e) => setImageTitle(e.target.value)} placeholder="예: 강남 A 프로젝트 3일차" required className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" disabled={isUploading} /></div>
                <div><label htmlFor="imageCategory" className="block text-sm font-medium text-gray-700 dark:text-gray-300">카테고리</label><select id="imageCategory" value={imageCategory} onChange={(e) => setImageCategory(e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" disabled={isUploading}><option>지붕공사</option><option>AS</option></select></div>
                <div><label htmlFor="imageDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300">내용 (선택 사항)</label><textarea id="imageDescription" rows="4" value={imageDescription} onChange={(e) => setImageDescription(e.target.value)} placeholder="이미지에 대한 설명을 입력하세요..." className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-sky-500 focus:border-sky-500 sm:text-sm" disabled={isUploading}></textarea></div>
                <div className="flex justify-end space-x-3 pt-4"><button type="button" onClick={() => { if (!isUploading) setShowUploadModal(false); }} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-md" disabled={isUploading}>취소</button><button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 rounded-md disabled:opacity-50" disabled={isUploading}>{isUploading ? '업로드 중...' : '업로드'}</button></div>
              </form>
            </div>
          </div>
        )}

        {/* 수정 모달 */}
        {showEditModal && editingImage && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-white">이미지 정보 수정</h2>
              <form onSubmit={handleUpdateImage} className="space-y-4">
                <img src={editingImage.imageUrl} alt="수정할 이미지" className="max-h-40 rounded-md mb-4" />
                <div>
                  <label htmlFor="editImageTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300">제목 (필수)</label>
                  <input type="text" id="editImageTitle" value={imageTitle} onChange={(e) => setImageTitle(e.target.value)} required className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label htmlFor="editImageCategory" className="block text-sm font-medium text-gray-700 dark:text-gray-300">카테고리</label>
                  <select id="editImageCategory" value={imageCategory} onChange={(e) => setImageCategory(e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white"><option>지붕공사</option><option>AS</option></select>
                </div>
                <div>
                  <label htmlFor="editImageDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300">내용 (선택 사항)</label>
                  <textarea id="editImageDescription" rows="4" value={imageDescription} onChange={(e) => setImageDescription(e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-sky-500 focus:border-sky-500 sm:text-sm"></textarea>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-md">취소</button>
                  <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 rounded-md">저장</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {loading && images.length === 0 && pageDirection === 'initial' && <p className="text-center text-gray-500 dark:text-gray-400 py-4">이미지 목록을 불러오는 중...</p>}
        {!loading && images.length === 0 && totalImages === 0 && <p className="text-center text-gray-500 dark:text-gray-400 py-4">업로드된 이미지가 없습니다.</p>}

        {!loading && images.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6">
              {images.map(image => (
                <div key={image.id} className={`bg-white dark:bg-slate-800 rounded-lg shadow-md overflow-hidden border-2 ${image.isFeatured ? 'border-amber-500 dark:border-amber-400' : image.isHeroImage ? 'border-blue-500 dark:border-blue-400' : 'border-transparent'}`}>
                  {/* ... (이미지 및 정보 표시 부분) ... */}
                  <img src={image.imageUrl} alt={image.title} className="w-full h-48 object-cover"/>
                  <div className="p-4">
                    {image.isHeroImage && <div className="text-xs font-bold text-blue-500 dark:text-blue-400 mb-2">현재 배경 이미지</div>}
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white truncate" title={image.title}>{image.title}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{image.category}</p>
                    {/* ... (다른 정보들) ... */}

                    {/* 버튼 영역 */}
                    <div className="mt-3 flex justify-between items-center flex-wrap gap-2">
                        {/* 대표 프로젝트 토글 */}
                        <button onClick={() => toggleFeaturedImage(image.id, image.isFeatured || false)} className={`text-xs px-2 py-1 rounded ${image.isFeatured ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500 text-slate-700 dark:text-slate-200'}`}>
                            {image.isFeatured ? '대표 해제' : '대표 지정'}
                        </button>

                        {/* 배경 이미지 지정 버튼 */}
                        <button onClick={() => setAsHeroImage(image)} disabled={image.isHeroImage} className="text-xs px-2 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white disabled:bg-blue-300 dark:disabled:bg-blue-800 disabled:cursor-not-allowed">
                            배경으로 지정
                        </button>
                    </div>
                    <div className="mt-2 flex justify-end space-x-2">
                        <button className="text-xs px-2 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded" onClick={() => openEditModal(image)}>수정</button>
                        <button className="text-xs px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded" onClick={() => handleDelete(image.id, image.fileName)}>삭제</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-8 flex justify-center">
                <nav className="flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button onClick={handlePrevPage} disabled={currentPage === 1 || loading} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50">
                    <ChevronLeftIcon /> 이전
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm font-medium text-gray-700 dark:text-gray-300">
                    페이지 {currentPage} / {totalPages > 0 ? totalPages : 1}
                  </span>
                  <button onClick={handleNextPage} disabled={currentPage === totalPages || loading} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50">
                    다음 <ChevronRightIcon />
                  </button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
export default ImageManagementPage;
